import { useEffect, useMemo } from 'react';

import { useIzteachSessionStore } from '../../stores/izteach';
import { useQltSlotAttendanceStore, useQltTimetableStore } from '../../stores/qlt';
import { resolvePeriodCourseScope } from '../periodLabels';
import { rosterOf } from '../submissions/submissions';
import type { BoardContextValue } from '../useBoardContext';
import { examLoadKey } from './classExams';
import { buildExamResults, examCardView, groupExams, type ExamAttempt } from './examResults';
import { useExamResultStore } from './store';

/** The class's exam attempts for this period; the store skips a load it already holds. */
export function useClassExamLoad(context: Pick<BoardContextValue, 'classId' | 'gradeId' | 'subjectId' | 'date' | 'index'>) {
  const cells = useQltTimetableStore((state) => state.todayCells);
  // a board opened by URL takes grade and subject from the timetable
  const scope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const signedIn = useIzteachSessionStore((state) => Boolean(state.accessToken));
  const codes = useMemo(() => rosterOf(rows).map((student) => student.code), [rows]);
  const input = { codes, classId: context.classId, gradeId: scope.gradeId, subjectId: scope.subjectId };
  const key = examLoadKey(input, signedIn);
  // a roster or token arriving later loads again
  useEffect(() => { if (codes.length > 0) void useExamResultStore.getState().load(key, input); }, [key]);
  return { reload: () => void useExamResultStore.getState().load(key, input, true), scope };
}

/** One exam's class results, its questions and the Xếp hạng order a sheet steps through. */
export function useExamResultsView(examId: string) {
  const attempts = useExamResultStore((state) => state.attempts);
  const loading = useExamResultStore((state) => state.loading);
  const loadError = useExamResultStore((state) => state.error);
  const entry = useExamResultStore((state) => state.questions[examId]);
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const roster = useMemo(() => rosterOf(rows), [rows]);
  const questions = entry ? entry.questions : [];

  useEffect(() => { void useExamResultStore.getState().loadQuestions(examId); }, [examId]);

  // a test unit opens this before its class's attempts loaded, or with none at all
  const view = useMemo(() => examCardView(groupExams(attempts, roster), examId, { loading: loading || (rosterLoading && roster.length === 0), error: loadError }), [attempts, roster, examId, loading, rosterLoading, loadError]);
  const exam = view.kind === 'ready' ? view.exam : null;
  const results = useMemo(() => (exam ? buildExamResults(exam, roster, questions) : null), [exam, roster, questions]);
  const order = useMemo<ExamAttempt[]>(() => (exam && results ? results.leaders.map((row) => exam.byCode[row.code]).filter(Boolean) : []), [exam, results]);
  const names = useMemo(() => {
    const out: Record<string, string> = {};
    roster.forEach((student) => { out[student.code] = student.name; });
    return out;
  }, [roster]);

  return {
    view,
    exam,
    results,
    order,
    names,
    questions,
    questionsLoading: !entry || entry.loading,
    questionsError: entry ? entry.error : null,
    retryQuestions: () => void useExamResultStore.getState().loadQuestions(examId, true),
  };
}
